import { useCallback, useEffect } from 'react';
import { useMaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { Box, IconButton, MenuItem, Tooltip } from '@mui/material';
import IconTrash from 'src/presentations/components/atom/icons/Trash';
import IconNewEdit from 'src/presentations/components/atom/icons/newedit.svg';
import { CustomTable } from 'src/presentations/components/molecules/table/Table';
import { useFormContext } from 'react-hook-form';
import { ContainerChargeDto } from 'src/generated/services/TFinancialApi';
import { beautifyEnum, formatNumber } from 'src/utils/utility';
import { createTableConfig } from 'src/presentations/utils/tableConfigUtils';
import { TableFieldNumber } from './TableFieldNumber';
import { NoContractForm } from './types';
import { getCommonPrefixColumns, getCommonSuffixColumns } from './commonChargeColumns';

// Common currencies used in freight forwarding
const CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'CNY', 'SGD', 'AUD', 'CAD', 'CHF', 'HKD', 'INR', 'MYR', 'THB', 'KRW'];

const firstCellStyles = {
  boxShadow: '4px 0px 12px 0px rgba(89, 114, 130, 0.08)',
  borderLeft: '1px solid transparent',
  width: '120px',
  padding: '0 10px 0 10px !important',

  '& .MuiBox-root': {
    gap: '0!important',
    width: '90px',
    justifyContent: 'center',

    '& svg': {
      width: '20px!important',
    },
  },
};

const useChargesContainerColumns = (): MRT_ColumnDef<ContainerChargeDto>[] => {
  const commonPrefix = getCommonPrefixColumns<ContainerChargeDto>();
  const commonSuffix = getCommonSuffixColumns<ContainerChargeDto>();

  // Container-specific columns: Unit Price 20, Unit Price 40, Currency, Container Type
  const containerSpecific: MRT_ColumnDef<ContainerChargeDto>[] = [
    {
      accessorKey: 'unitPrice20',
      header: 'Unit Price 20',
      Cell: ({ row }) => {
        const value = row?.original?.unitPrice20;
        if (value !== null && value !== undefined) {
          return <span className='text-14 xl:text-16'>{formatNumber(parseFloat(value.toString()))}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
      muiEditTextFieldProps: {
        type: 'number',
        required: true,
      },
    },
    {
      accessorKey: 'unitPrice40',
      header: 'Unit Price 40',
      Cell: ({ row }) => {
        const value = row?.original?.unitPrice40;
        if (value !== null && value !== undefined) {
          return <span className='text-14 xl:text-16'>{formatNumber(parseFloat(value.toString()))}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
      muiEditTextFieldProps: {
        type: 'number',
        required: true,
      },
    },
    {
      accessorKey: 'currency',
      header: 'Currency',
      Cell: ({ row }) => <span className='text-14 xl:text-16'>{row?.original?.currency || '-'}</span>,
      muiEditTextFieldProps: {
        select: true,
        required: true,
        children: CURRENCIES.map((currency) => (
          <MenuItem key={currency} value={currency}>
            {currency}
          </MenuItem>
        )),
      },
    },
    {
      accessorKey: 'containerType',
      header: 'Container Type',
      Cell: ({ row }) => <span className='text-14 xl:text-16 capitalize'>{row?.original?.containerType ? beautifyEnum(row.original.containerType) : '-'}</span>,
      muiEditTextFieldProps: {
        type: 'text',
        required: true,
      },
    },
  ];

  return [...commonPrefix, ...containerSpecific, ...commonSuffix];
};

export interface ITableChargesContainerProps {
  data: ContainerChargeDto[];
  enableEditing?: boolean;
  editingRowIndex?: number | null;
  onEditingRowChange?: (index: number | null) => void;
  onEditingChange?: (isEditing: boolean) => void;
}

export const TableChargesContainer = ({ data, enableEditing = false, editingRowIndex, onEditingRowChange, onEditingChange }: ITableChargesContainerProps) => {
  const columns = useChargesContainerColumns();
  const { setValue } = useFormContext<NoContractForm>();

  const baseTableConfig = createTableConfig<ContainerChargeDto>({
    onCreatingCancel: () => {},
    onEditingCancel: () => {},
  });

  const table = useMaterialReactTable({
    ...baseTableConfig,
    data,
    enableRowNumbers: false,
    enableSorting: false,
    enablePinning: false,
    enableRowDragging: false,
    enableColumnActions: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableEditing,
    editDisplayMode: 'row',
    enableColumnPinning: true,
    positionActionsColumn: 'first',
    initialState: {
      columnPinning: { left: ['mrt-row-actions'] },
    },
    onEditingRowSave: ({ row, values }) => {
      // Update the row data - merge with original to preserve non-editable fields
      const newData = [...data];
      newData[row.index] = { ...row.original, ...values };
      setValue('containerCharges', newData);
      table.setEditingRow(null);
      if (onEditingChange) onEditingChange(false);
    },
    onEditingRowCancel: () => {
      table.setEditingRow(null);
      if (onEditingChange) onEditingChange(false);
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        size: 120,
      },
    },
    muiTableProps: {
      sx: {
        borderCollapse: 'separate',
      },
    },
    muiTableHeadProps: {
      sx: {
        '& th:first-child': firstCellStyles,
      },
    },
    muiTableBodyProps: {
      sx: {
        '& td:first-child': firstCellStyles,
      },
    },
    muiTableBodyCellProps: {
      sx: {
        borderBottom: '1px solid #DFDFDF !important',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        borderBottom: '1px solid #DFDFDF !important',
      },
    },
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex' }}>
        <Tooltip title='Edit'>
          <IconButton
            onClick={() => {
              table.setEditingRow(row);
              if (onEditingChange) onEditingChange(true);
            }}
          >
            <img src={IconNewEdit} alt='Edit' />
          </IconButton>
        </Tooltip>
        <Tooltip title='Delete'>
          <IconButton
            color='error'
            onClick={() => {
              const newData = data.filter((_, index) => index !== row.index);
              setValue('containerCharges', newData);
            }}
          >
            <IconTrash />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    columns,
  });

  // Trigger edit mode when editingRowIndex changes
  useEffect(() => {
    if (editingRowIndex !== null && editingRowIndex !== undefined && data[editingRowIndex]) {
      // Get all rows and find the one at the specified index
      const { rows } = table.getRowModel();
      const row = rows[editingRowIndex];
      if (row) {
        table.setEditingRow(row);
        if (onEditingChange) onEditingChange(true);
        if (onEditingRowChange) {
          setTimeout(() => onEditingRowChange(null), 100);
        }
      }
    }
  }, [editingRowIndex, data, table, onEditingRowChange, onEditingChange]);

  return <CustomTable table={table} />;
};
